import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import CommonCardHeader from '../../../../../../coreComponents/CommonCardHeader';
import {
  useAddLessonVideoMutation,
  useDeleteLessonVideoMutation,
  useGetSingleLessonQuery,
  useTrainLessonMutation,
} from '../../../../../../reduxToolkit/reducers/usersSlice';
import { useEffect, useMemo, useState } from 'react';
import ChatHistory from './ChatHistory';
import SimpleModal from '../../../../../uiKits/modal/basicModal/SimpleModal';
import Breadcrumbs from '../../../../../../coreComponents/breadcrumbs';
import Loader from '../../../../../../layout/Loader';
import { useTranslation } from 'react-i18next';
import { CourseCategoryTranslations } from '../../../../../../types/Tables';

import VideoJSPlayer from '../../../../../videoPlayer/VideoJSPlayer';
import { Image } from '../../../../../../coreComponents/Image';
import { baseLang } from '../../../../../../lib/utils';
import { LessonVideo } from '../../../../../../types/Courses';
import Swal from 'sweetalert2';
import { getSocketSessionId } from '../../../../../../hooks/useSocket';
import CommonFileUpload from '../../../../../../coreComponents/CommonFileUpload';
import CommonModal from '../../../../../uiKits/modal/common/CommonModal';
import CommonProgress from '../../../../../uiKits/progress/CommonProgress';
import { uploadInChunks } from '../../../../../../services/uploadInChunks';
import { Playlist } from '../../../../../videoPlayer/Playlist';
import { sortPlaylist } from '../../../../../videoPlayer/playlistHelper';
import { VideoNamesInputs } from './VideoNamesInputs';

const LessonDetailsPage = () => {
  const { lesson_id } = useParams<{ lesson_id: string }>();
  const navigate = useNavigate();
  const { data, isFetching, isSuccess, status, refetch } = useGetSingleLessonQuery({
    lesson_id,
  });
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [currentLessonTitle, setCurrentLessonTitle] = useState('');
  const [currentDescription, setCurrentDesciption] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<{ name: string; link: string }[]>([]);
  const [selectPlayer, setSelectPlayer] = useState(0);
  const [playlist, setPlaylist] = useState<LessonVideo[]>([]);
  const [currentVideo, setCurrentVideo] = useState<LessonVideo | null>(null);
  const [openAddFiles, setOpenAddFiles] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File[] | null>(null);
  const [selectedFileNames, setSelectedFileNames] = useState<{
    filename_en?: string;
    filename_uk?: string;
    filename_ru?: string;
    playlist_pos?: number;
  }>({
    filename_en: '',
    filename_uk: '',
    filename_ru: '',
    playlist_pos: undefined,
  });
  const [progressData, setProgressData] = useState([
    { text: 'Uploading...', class: 'primary', value: '0' },
  ]);
  const { t, i18n } = useTranslation();
  const pageLang = baseLang(i18n.language);

  const [deleteLessonVideo] = useDeleteLessonVideoMutation();
  const [trainLesson] = useTrainLessonMutation();
  const [addVideoToLesson] = useAddLessonVideoMutation();
  useEffect(() => {
    setProgressData((prev) => {
      const newData = [...prev];
      if (currentChunk > 0 && totalChunks > 0) {
        const percentage = Math.round((currentChunk / totalChunks) * 100);
        newData[0].value = percentage.toString();
        return newData;
      }
      return prev;
    });
  }, [currentChunk, totalChunks]);

  const showNotificationNoVideoUrl = (videoTitle: string) => {
    Swal.fire({
      icon: 'info',
      title: t('courses.lessons.detailsPage.noVideoUrlTitle', { videoTitle }),
      text: t('courses.lessons.detailsPage.noVideoUrlText', { videoTitle }),
    });
  };

  useEffect(() => {
    
    if (data?.video.length > 0 && currentVideo && !currentVideo?.videoUrl) {
      showNotificationNoVideoUrl(getCurrentVideoLocalizedTitle());
    }
  }, [currentVideo, data?.video, i18n.language]);

  useEffect(() => {
    if (status === 'fulfilled' && data) {
      if (pageLang === 'en') {
        setCurrentLessonTitle(data.title);
        setCurrentDesciption(data.description);
        setBreadcrumbs([
          {
            name: t('courses.coursesList.coursesListPageTitle'),
            link: '/courses/courses-list',
          },
          {
            name: data.course.name,
            link: `/courses/details/${data.course.id}`,
          },
        ]);
      } else {
        const lessonTranslation = data.translations.find(
          (el: { description: string; id: number; lang: string; name: string }) =>
            el.lang === pageLang,
        );
        const courseTranslations = data.course.translations.find(
          (tr: CourseCategoryTranslations) => tr.lang === pageLang,
        );

        setCurrentLessonTitle(
          lessonTranslation?.title ?? data.title, // fallback to original
        );
        setCurrentDesciption(lessonTranslation?.description ?? data.description);

        setBreadcrumbs([
          {
            name: t('courses.coursesList.coursesListPageTitle'),
            link: '/courses/courses-list',
          },
          {
            name: courseTranslations?.name ?? data.course.name,
            link: `/courses/details/${data.course.id}`,
          },
        ]);
      }

      if (data.video.length > 0) {
        const sortedVideos = sortPlaylist(data.video);
        setPlaylist(data.video.length > 0 ? sortedVideos : []);
        setCurrentVideo(data.video.length > 0 ? sortedVideos[0] : null);
      }

      if (data.video?.length > 0 && data.video[0]?.link) {
        setSelectPlayer(1);
      } else {
        setSelectPlayer(0);
      }
    }
  }, [data, i18n, i18n.language, status]);

  const takenPlaylistPositions = useMemo(() => {
    const positions =
      data?.video
        .map((video: LessonVideo) => video.playlist_pos)
        .filter((pos: number | null): pos is number => pos !== null)
        .sort((a: number, b: number) => a - b) ?? [];
    return positions;
  }, [data?.video]);

  const deleteVideoHandller = (video: LessonVideo) => {
    Swal.fire({
      icon: 'warning',
      title: t('courses.lessons.detailsPage.deleteVideoTitle'),
      text: t('courses.lessons.detailsPage.deleteVideoText'),
      showCancelButton: true,
      confirmButtonText: t('courses.lessons.detailsPage.deleteVideoConfirm'),
      cancelButtonText: t('courses.lessons.detailsPage.deleteVideoCancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = { videoId: video.id, lessonId: data.id };
        try {
          deleteLessonVideo(payload);
          Swal.fire({
            icon: 'success',
            title: t('courses.lessons.detailsPage.deleteVideoSuccessTitle'),
            text: t('courses.lessons.detailsPage.deleteVideoSuccessText'),
            showConfirmButton: true,
          });
        } catch (error) {
          console.error('Failed to delete video:', error);
          Swal.fire({
            icon: 'error',
            title: t('courses.lessons.detailsPage.deleteVideoErrorTitle'),
            text: t('courses.lessons.detailsPage.deleteVideoErrorText'),
          });
        }
      }
    });
  };

  const handleTrainLesson = async (lessonId: number) => {
    const sessionId = getSocketSessionId();
    try {
      await trainLesson({ lessonId, sessionId }).unwrap?.();
      // if (refetch) await refetch();
      Swal.fire({
        icon: 'success',
        title: t('courses.lessons.detailsPage.trainLessonSuccessTitle'),
        text: t('courses.lessons.detailsPage.trainLessonSuccessText'),

        showConfirmButton: true,
      });
    } catch (error) {
      console.error('Failed to train lesson:', error);
      Swal.fire({
        icon: 'error',
        title: t('courses.lessons.detailsPage.trainLessonErrorTitle'),
        text: t('courses.lessons.detailsPage.trainLessonErrorText'),
      });
    }
  };

  const handleUploadNewVideo = async (
    newFile: File,
    fileNames: { [key: string]: string | undefined | number },
  ) => {
    setOpenAddFiles(false);
    new Promise((resolve) => setTimeout(resolve, 500));
    setShowProgress(true);

    const uploadResult = await uploadInChunks(
      newFile,
      20 * 1024 * 1024,
      (current: number, total: number) => {
        setCurrentChunk(current);
        setTotalChunks(total);
      },
    );
    console.log('Upload result:', uploadResult);
    setShowProgress(false);
    setProgressData([{ text: 'Uploading...', class: 'primary', value: '0' }]);
    setCurrentChunk(0);
    setTotalChunks(0);
    if (!uploadResult.filePath) {
      console.error('❌ No filePath returned from upload');
      return { uploaded: false };
    }

    const payload = {
      lessonId: data.id,
      sessionId: getSocketSessionId(),
      videoFilePath: uploadResult.filePath,
      filename_en: fileNames.filename_en,
      filename_uk: fileNames.filename_uk,
      filename_ru: fileNames.filename_ru,
      playlist_pos: fileNames.playlist_pos,
    };

    try {
      const res = await addVideoToLesson(payload).unwrap();
      console.log('res addVideoToLesson', res);
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: t('courses.lessons.detailsPage.uploadVideoSuccessTitle'),
          text: t('courses.lessons.detailsPage.uploadVideoSuccessText'),

          showConfirmButton: true,
        });
        // if (refetch) await refetch();
      } else {
        Swal.fire({
          icon: 'error',
          title: t('courses.lessons.detailsPage.uploadVideoErrorTitle'),
          text: t('courses.lessons.detailsPage.uploadVideoErrorText'),
        });
      }
    } catch (err) {
      setSelectedFile(null);
      setSelectedFileNames({
        filename_en: '',
        filename_uk: '',
        filename_ru: '',
        playlist_pos: undefined,
      });
      Swal.fire({
        icon: 'error',
        title: t('courses.lessons.detailsPage.uploadVideoErrorTitle'),
        text: t('courses.lessons.detailsPage.uploadVideoErrorText'),
      });
      console.error('Upload error:', err);
      return { uploaded: false };
    }
  };

  const getCurrentVideoLocalizedTitle = () => {
    if (!currentVideo) return '';
    switch (pageLang) {
      case 'en':
        return currentVideo?.filename_en ?? currentVideo.filename;
      case 'uk':
        return currentVideo?.filename_uk ?? currentVideo.filename;
      case 'ru':
        return currentVideo?.filename_ru ?? currentVideo.filename;
      default:
        return currentVideo.filename;
    }
  };

  return (
    <>
      {isFetching && <Loader />}

      {status === 'fulfilled' && data && (
        <>
          <Breadcrumbs
            mainTitle={currentLessonTitle}
            // parent={t('courses.generalTitle')}
            parentsArray={breadcrumbs}
          />
          <Container fluid>
            <Row>
              <Col sm="12">
                <Card>
                  <div className="p-3">
                    <Button
                      color="primary"
                      onClick={() => navigate(`/courses/details/${data.courseId}`)}
                    >
                      ← {t('courses.lessons.detailsPage.backToCourse')}
                    </Button>
                  </div>
                  <CommonCardHeader title={currentLessonTitle} tag />

                  <CardBody>
                    <Row>
                      <Col md="8">
                        <p className="mb-4">
                          {t('courses.lessons.detailsPage.detailedLessonTitle', {
                            title: currentLessonTitle,
                          })}
                        </p>
                        <h5 className="mb-3">
                          {t('courses.lessons.detailsPage.detailedLessonCourseTitle', {
                            name: data?.course.name,
                          })}
                        </h5>
                        <h5 className="mb-3">
                          {t('courses.lessons.detailsPage.detailedLessonVideoFile', {
                            filename: currentVideo?.filename,
                          })}
                        </h5>
                        <h5 className="mb-3">
                          {t('courses.lessons.detailsPage.detailedLessonConversationId', {
                            conversationId: data?.conversationId ? data?.conversationId : 'Not trained',
                          })}
                          {!data?.conversationId && (
                            <Button
                              color="warning"
                              outline
                              size="sm"
                              onClick={() => handleTrainLesson(data.id)}
                              className="ms-2"
                              disabled={data.isCurrentlyProcessingVideo}
                            >
                              <i className="fa fa-external-link-square"></i> {t('video.train')}
                            </Button>
                          )}
                        </h5>
                        <h5 className="mb-3">
                          {t('video.tokenUsage', {
                            tokens: playlist.reduce(
                              (acc, video) => acc + video.transcriptionTokens,
                              0,
                            ),
                            maxTokens: 100000,
                          })}
                        </h5>
                        <h5 className="mb-3">
                          {t('video.videoInLesson', { videoCount: playlist.length, maxVideos: 10 })}
                        </h5>
                        <Button onClick={() => setShowChatHistory(true)}>
                          {' '}
                          {t('courses.lessons.detailsPage.openChatHistory')}
                        </Button>
                      </Col>
                      <Col md="4" className="d-flex justify-content-center align-items-center">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_STATIC}/coursesPics/${data?.lessonCover}`} // or `data.lessonCoverPath`
                          alt="Lesson cover"
                          className="img-fluid"
                          style={{ maxHeight: '200px', objectFit: 'contain' }}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <SimpleModal isOpen={showChatHistory} close={() => setShowChatHistory(false)}>
                  <ChatHistory
                    threadId={data?.threadId}
                    videoId={currentVideo?.id}
                    visible={showChatHistory}
                  />
                </SimpleModal>
              </Col>
            </Row>
            <Row>
              <Col sm="12">
                <Card>
                  <CardBody>
                    <h3 className="mb-2">
                      {' '}
                      {t('courses.lessons.detailsPage.video', {
                        videoTitle: getCurrentVideoLocalizedTitle(),
                      })}
                    </h3>
                    <Row className="mb-2">
                      <Col xs="12">
                        <p>
                          {' '}
                          {t('video.playsVideo', {
                            current:
                              playlist.findIndex((video) => video.id === currentVideo?.id) + 1,
                            total: playlist.length,
                          })}
                        </p>
                        {/* {playlist.findIndex((video) => video.id === currentVideo?.id) + 1} of{' '}
                        {playlist.length} */}
                      </Col>
                      <Col sm="12">
                        <Button
                          className="me-2"
                          color="primary"
                          outline
                          disabled={currentVideo?.id === playlist[0]?.id} // Дизейблимо, якщо це перше відео
                          onClick={() => {
                            const currentIndex = playlist.findIndex(
                              (video) => video.id === currentVideo?.id,
                            );
                            const prevIndex =
                              (currentIndex - 1 + playlist.length) % playlist.length;
                            setCurrentVideo(playlist[prevIndex]);
                          }}
                        >
                          <i className="fa fa-step-backward"></i>
                        </Button>

                        <Button
                          color="primary"
                          outline
                          disabled={currentVideo?.id === playlist[playlist.length - 1]?.id} // Дизейблимо, якщо це останнє відео
                          onClick={() => {
                            const currentIndex = playlist.findIndex(
                              (video) => video.id === currentVideo?.id,
                            );
                            const nextIndex = (currentIndex + 1) % playlist.length;
                            setCurrentVideo(playlist[nextIndex]);
                          }}
                        >
                          <i className="fa fa-step-forward"></i>
                        </Button>
                      </Col>
                    </Row>
                    {playlist.length > 0 ? (
                      <div className="ratio ratio-16x9 mb-4">
                        <VideoJSPlayer
                          key={currentVideo?.id} // Force re-mount when video changes
                          src={
                            currentVideo?.link
                              ? currentVideo.link
                              : `${process.env.REACT_APP_BACKEND_API}/video/file/${currentVideo?.signedSrc}`
                          }
                        />
                      </div>
                    ) : (
                      <Image
                        className="img-fluid sm-100-w"
                        src={`${process.env.REACT_APP_BACKEND_STATIC}/coursesPics/${data?.lesson?.lessonCover}`}
                        alt=""
                      />
                    )}
                    <Row>
                      <Col>
                        <Col sm="6">
                          <Button color="primary" onClick={() => setOpenAddFiles(true)}>
                            {t('video.addVideoToLesson')}
                          </Button>
                        </Col>

                        <Playlist
                          playlist={playlist}
                          currentVideo={currentVideo}
                          onVideoSelect={(video) => setCurrentVideo(video)}
                          deleteVideoHandller={deleteVideoHandller}
                          notificationNoVideoUrl={showNotificationNoVideoUrl}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
          <CommonModal
            modalData={{
              isOpen: openAddFiles,
              // close: () => setOpenAddFiles(false),
              toggler: () => {
                setOpenAddFiles(false);
                setSelectedFile(null);
                setSelectedFileNames({
                  filename_en: '',
                  filename_uk: '',
                  filename_ru: '',
                });
              },
              size: 'lg',
              center: true,
            }}
          >
            <VideoNamesInputs
              selectedFileNames={selectedFileNames}
              setSelectedFileNames={setSelectedFileNames}
              takenPlaylistPositions={takenPlaylistPositions}
            />
            <CommonFileUpload
              onlyVideo
              accept={{ 'video/*': [] }}
              onFilesChange={(file) => setSelectedFile(file)}
            />
            {selectedFile && (
              <div className="p-3">
                <Button
                  color="primary"
                  disabled={
                    selectedFileNames.filename_en === '' ||
                    selectedFileNames.filename_uk === '' ||
                    selectedFileNames.filename_ru === '' ||
                    selectedFileNames.playlist_pos === undefined ||
                    selectedFileNames.playlist_pos === null ||
                    selectedFileNames.playlist_pos < 1 ||
                    selectedFileNames.playlist_pos > 10
                  }
                  onClick={() => handleUploadNewVideo(selectedFile[0], selectedFileNames)}
                >
                  {t('video.uploadSelectedVideo')}
                </Button>
              </div>
            )}
          </CommonModal>
          <SimpleModal isOpen={showProgress} close={() => setShowProgress(false)}>
            <CommonProgress
              title={t('video.uploadingVideo')}
              data={progressData}
              striped
              animated
              tag
              progressClass={'mb-4 text-center'}
              value
            />
          </SimpleModal>
        </>
      )}
    </>
  );
};

export default LessonDetailsPage;
